import { Injectable, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { normalizeError } from '@maximizer/shared/util';
import { MicrosoftAuthSyncService } from './microsoft-auth-sync.service';
import { MicrosoftTokenBundle } from '../models/microsoft-auth';
import {
  OutlookSyncOAuthFlowState,
  OutlookSyncOAuthStage,
} from '../models/outlook-sync-oauth-flow.model';
import { generateStateNonce } from '../utils/state-nonce.util';
import { OUTLOOK_SYNC_ENV } from '../config';
export const OUTLOOK_SYNC_OAUTH_STORAGE_KEY = 'olsync-oauth-flow';
export const OUTLOOK_SYNC_CALLBACK_QUERY_PARAM = 'olsync-stage';

/**
 * Reads the opener-handshake nonce from the persisted OAuth flow state, if a
 * flow is in progress. Used by the app bootstrap on a Microsoft callback URL
 * to re-handshake the browser-only token from the opener window.
 */
export function readPersistedHandshakeNonce(): string | null {
  try {
    const raw = sessionStorage.getItem(OUTLOOK_SYNC_OAUTH_STORAGE_KEY);
    if (!raw) return null;
    const state = JSON.parse(raw) as Partial<OutlookSyncOAuthFlowState>;
    return typeof state.handshakeNonce === 'string' ? state.handshakeNonce : null;
  } catch {
    return null;
  }
}

@Injectable({ providedIn: 'root' })
export class OutlookSyncOAuthFlowService {
  private readonly microsoftAuthSync = inject(MicrosoftAuthSyncService);
  private readonly env = inject(OUTLOOK_SYNC_ENV);

  readonly stage = signal<OutlookSyncOAuthStage>('idle');
  readonly errorMessage = signal<string | null>(null);
  readonly errorCode = signal<string | null>(null);
  readonly completedConfigurationId = signal<string | null>(null);

  readonly installCodeReady = signal<{
    code: string;
    state: OutlookSyncOAuthFlowState;
  } | null>(null);

  // Collapses caller-level dupes when re-entering the exchange with a single-use code.
  private readonly inFlightInstallExchange = new Map<
    string,
    Promise<MicrosoftTokenBundle>
  >();
  private readonly inFlightConfigExchange = new Map<string, Promise<boolean>>();

  // Captured once at bootstrap (from `?hs=` or the persisted flow state) so a
  // later Microsoft redirect can carry it in the flow state; the query param
  // may be stripped by router navigation before the redirect starts.
  private capturedHandshakeNonce: string | null = null;

  captureHandshakeNonce(nonce: string | null): void {
    if (nonce) {
      this.capturedHandshakeNonce = nonce;
    }
  }

  async startMicrosoftReauth(
    configurationId: string,
    maximizerUserUid: string,
  ): Promise<void> {
    try {
      const syncConfig = this.env.syncConfig;
      if (!syncConfig) {
        throw new Error('syncConfig not configured in environment.');
      }
      if (!configurationId || !maximizerUserUid) {
        throw new Error(
          'configurationId and maximizerUserUid are required for re-auth.',
        );
      }

      this.errorMessage.set(null);
      this.errorCode.set(null);

      const stateNonce = generateStateNonce();

      const state: OutlookSyncOAuthFlowState = {
        stage: 'ms-authorize',
        stateNonce,
        configurationId,
        maximizerUserUid,
        mode: 'reauth',
        handshakeNonce: this.capturedHandshakeNonce ?? undefined,
      };
      this.persist(state);
      this.stage.set('ms-authorize');

      const url = this.buildAuthorizeUrl({
        state: this.buildCallbackState('ms-callback', stateNonce),
        redirectUri: syncConfig.redirectUri,
      });
      window.location.href = url;
    } catch (error) {
      this.fail(`Failed to start re-auth: ${normalizeError(error)}`);
    }
  }

  async startInstallFlow(
    maximizerUserUid: string,
    maximizerUserEmail: string,
    workspaceId: string,
    correlationId: string,
    tenantAlias: string,
    tenantId: string,
    reinstallConfigurationId?: string,
  ): Promise<void> {
    const syncConfig = this.env.syncConfig;
    if (!syncConfig) {
      throw new Error('syncConfig not configured in environment.');
    }
    if (
      !maximizerUserUid ||
      !maximizerUserEmail ||
      !workspaceId ||
      !correlationId ||
      !tenantAlias ||
      !tenantId
    ) {
      throw new Error(
        'maximizerUserUid, maximizerUserEmail, workspaceId, correlationId, tenantAlias, and tenantId are required to start the install flow.',
      );
    }

    this.errorMessage.set(null);
    this.errorCode.set(null);

    const stateNonce = generateStateNonce();
    const isReinstall = !!reinstallConfigurationId;

    const state: OutlookSyncOAuthFlowState = {
      stage: 'ms-authorize',
      stateNonce,
      maximizerUserUid,
      mode: isReinstall ? 'reinstall' : 'install',
      configurationId: isReinstall ? reinstallConfigurationId : undefined,
      maximizerUserEmail,
      workspaceId,
      correlationId,
      tenantAlias,
      tenantId,
      handshakeNonce: this.capturedHandshakeNonce ?? undefined,
    };
    this.persist(state);
    this.stage.set('ms-authorize');

    const url = this.buildAuthorizeUrl({
      state: this.buildCallbackState('install-callback', stateNonce),
      redirectUri: syncConfig.redirectUri,
    });
    window.location.href = url;
  }

  async resumeFromUrl(): Promise<void> {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const returnedState = params.get('state');
    const errorCode = params.get('error');
    const error = params.get('error_description') ?? errorCode;

    const stage =
      params.get(OUTLOOK_SYNC_CALLBACK_QUERY_PARAM) ??
      ((code || error) && returnedState ? returnedState.split(':')[0] : null);

    if (!stage) {
      return;
    }

    const persisted = this.readState();
    if (
      persisted &&
      !this.isCallbackStateValid(returnedState, stage, persisted.stateNonce)
    ) {
      this.clearOauthParamsFromUrl();
      this.fail('State validation failed. Possible CSRF attempt.', {
        clearState: false,
      });
      return;
    }

    this.clearOauthParamsFromUrl();

    if (error) {
      this.fail(error, { code: errorCode });
      return;
    }
    if (!persisted) {
      this.fail('No OAuth flow state found. Start the flow again.');
      return;
    }
    if (!code) {
      this.fail('Missing authorization code in callback.');
      return;
    }

    if (stage === 'ms-callback') {
      await this.handleMsCallback(code, persisted);
    } else if (stage === 'install-callback') {
      this.handleInstallCallback(code, persisted);
    } else {
      this.fail(`Unknown callback stage: ${stage}`);
    }
  }

  async exchangeCodeForConfiguration(
    code: string,
    configurationId: string,
  ): Promise<boolean> {
    const syncConfig = this.env.syncConfig;
    if (!syncConfig) {
      throw new Error('syncConfig not configured.');
    }
    const key = `${configurationId}:${code}`;
    const existing = this.inFlightConfigExchange.get(key);
    if (existing) {
      return existing;
    }
    const pending = firstValueFrom(
      this.microsoftAuthSync.exchangeOAuthCode(code, syncConfig.redirectUri, configurationId),
    ).finally(() => this.inFlightConfigExchange.delete(key));
    this.inFlightConfigExchange.set(key, pending);
    return pending;
  }

  async exchangeInstallCode(code: string): Promise<MicrosoftTokenBundle> {
    const syncConfig = this.env.syncConfig;
    if (!syncConfig) {
      throw new Error('syncConfig not configured.');
    }
    const existing = this.inFlightInstallExchange.get(code);
    if (existing) {
      return existing;
    }
    const pending = firstValueFrom(
      this.microsoftAuthSync.exchangeCodeForTokens(code, syncConfig.redirectUri),
    ).finally(() => this.inFlightInstallExchange.delete(code));
    this.inFlightInstallExchange.set(code, pending);
    return pending;
  }

  private async handleMsCallback(
    code: string,
    state: OutlookSyncOAuthFlowState,
  ): Promise<void> {
    if (!state.configurationId) {
      return this.fail('Re-auth callback missing configurationId in flow state.');
    }
    this.stage.set('ms-callback');

    try {
      const ok = await this.exchangeCodeForConfiguration(
        code,
        state.configurationId,
      );

      this.completedConfigurationId.set(state.configurationId);
      this.clearState();
      if (ok) {
        this.stage.set('ready');
      } else {
        this.fail('Failed to exchange OAuth code.');
      }
    } catch (error) {
      this.fail(`MS re-auth failed: ${normalizeError(error)}`);
    }
  }

  private handleInstallCallback(
    code: string,
    state: OutlookSyncOAuthFlowState,
  ): void {
    this.stage.set('install-callback');
    this.clearState();
    if (this.installCodeReady() !== null) {
      return;
    }
    this.installCodeReady.set({ code, state });
  }

  /** The URL `state` param carries only the CSRF nonce, so callback telemetry has to read the correlation id from the persisted flow. */
  peekCorrelationId(): string | null {
    return this.readState()?.correlationId ?? null;
  }

  private persist(state: OutlookSyncOAuthFlowState): void {
    sessionStorage.setItem(OUTLOOK_SYNC_OAUTH_STORAGE_KEY, JSON.stringify(state));
  }

  private readState(): OutlookSyncOAuthFlowState | null {
    const raw = sessionStorage.getItem(OUTLOOK_SYNC_OAUTH_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    try {
      return JSON.parse(raw) as OutlookSyncOAuthFlowState;
    } catch {
      return null;
    }
  }

  private clearState(): void {
    sessionStorage.removeItem(OUTLOOK_SYNC_OAUTH_STORAGE_KEY);
  }

  private buildAuthorizeUrl(params: {
    state: string;
    redirectUri: string;
  }): string {
    const ms = this.env.syncConfig?.microsoft;
    if (!ms?.clientId || !ms.tenantId) {
      throw new Error(
        'MS OAuth is not configured. Set environment.syncConfig.microsoft.{tenantId,clientId}.',
      );
    }
    const query = new URLSearchParams({
      client_id: ms.clientId,
      response_type: 'code',
      redirect_uri: params.redirectUri,
      response_mode: 'query',
      scope: ms.scopes.join(' '),
      state: params.state,
      prompt: 'select_account',
    });
    return `https://login.microsoftonline.com/${ms.tenantId}/oauth2/v2.0/authorize?${query.toString()}`;
  }

  private buildCallbackState(stage: string, nonce: string): string {
    return `${stage}:${nonce}`;
  }

  private isCallbackStateValid(
    returned: string | null,
    expectedStage: string,
    expectedNonce: string,
  ): boolean {
    if (!returned) return false;
    return returned === this.buildCallbackState(expectedStage, expectedNonce);
  }

  private clearOauthParamsFromUrl(): void {
    const url = new URL(window.location.href);
    [
      OUTLOOK_SYNC_CALLBACK_QUERY_PARAM,
      'code',
      'state',
      'error',
      'error_description',
      'session_state',
    ].forEach((param) => url.searchParams.delete(param));
    window.history.replaceState({}, document.title, url.toString());
  }

  private fail(
    message: string,
    options: { clearState?: boolean; code?: string | null } = {},
  ): void {
    console.error('[olsync-oauth-flow]', message);
    if (options.clearState !== false) {
      this.clearState();
    }
    this.errorMessage.set(message);
    this.errorCode.set(options.code ?? null);
    this.stage.set('error');
  }
}
